import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import DataTable from "../components/DataTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDateTime } from "../utils/format";

const ROLE_LABELS = { admin: "Administrateur", direction: "Direction", comptable: "Comptable", commercial: "Commercial", gestionnaire: "Gestionnaire", location: "Location", caissier: "Caissier", mecanicien: "Mécanicien / Parc" };

export default function Users() {
  const [rows, setRows] = useState([]);
  const [activity, setActivity] = useState([]);
  const [open, setOpen] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "commercial", password: "" });

  const load = () => {
    api.get("/users").then((r) => setRows(r.data)).catch(() => {});
    api.get("/activity").then((r) => setActivity(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("Nom, email et mot de passe requis");
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", role: "commercial", password: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/users/${editFor.id}`, {
        name: editFor.name, phone: editFor.phone, role: editFor.role, active: editFor.active,
        ...(editFor.newPassword ? { password: editFor.newPassword } : {}),
      });
      toast.success("Utilisateur mis à jour");
      setEditFor(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Utilisateurs & permissions</h1>
          <p className="mt-1 text-sm text-slate-500">Rôles : Admin (tout), Direction (rapports/finance), Comptable (finance), Commercial (ventes), Location (locations), Caissier (encaissements), Gestionnaire & Mécanicien (parc/entretien).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-user-button"><Plus className="mr-1 h-4 w-4" /> Nouvel utilisateur</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Rôle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Mot de passe *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="user-submit">Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="users-table" rows={rows}
        searchKeys={["name", "email", "role"]}
        columns={[
          { key: "name", label: "Nom", render: (r) => <span className="font-semibold text-slate-800">{r.name}</span> },
          { key: "email", label: "Email" },
          { key: "phone", label: "Téléphone", render: (r) => r.phone || "—" },
          { key: "role", label: "Rôle", render: (r) => <Badge variant="outline" className="capitalize">{ROLE_LABELS[r.role] || r.role}</Badge> },
          { key: "active", label: "Statut", render: (r) => r.active
            ? <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Actif</span>
            : <span className="rounded-full bg-red-50 border border-red-200 px-2.5 py-0.5 text-xs font-semibold text-red-700">Inactif</span> },
          { key: "actions", label: "", sortable: false, render: (r) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditFor({ ...r })} data-testid={`edit-user-${r.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-red-500" data-testid={`delete-user-${r.id}`}
                onClick={async () => { if (window.confirm(`Supprimer ${r.name} ?`)) { try { await api.delete(`/users/${r.id}`); load(); } catch (e) { toast.error(fmtErr(e)); } } }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ) },
        ]}
      />

      <Dialog open={!!editFor} onOpenChange={() => setEditFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier — {editFor?.name}</DialogTitle></DialogHeader>
          {editFor && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nom</Label><Input value={editFor.name} onChange={(e) => setEditFor({ ...editFor, name: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={editFor.phone || ""} onChange={(e) => setEditFor({ ...editFor, phone: e.target.value })} /></div>
              <div><Label>Rôle</Label>
                <Select value={editFor.role} onValueChange={(v) => setEditFor({ ...editFor, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Statut</Label>
                <Select value={editFor.active ? "actif" : "inactif"} onValueChange={(v) => setEditFor({ ...editFor, active: v === "actif" })}>
                  <SelectTrigger data-testid="edit-user-active"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="actif">Actif</SelectItem><SelectItem value="inactif">Inactif</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Nouveau mot de passe (optionnel)</Label><Input type="password" value={editFor.newPassword || ""} onChange={(e) => setEditFor({ ...editFor, newPassword: e.target.value })} data-testid="edit-user-password" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="edit-user-submit">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div>
        <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Journal d'activité</h3>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="activity-log">
          {activity.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-400">Aucune activité enregistrée.</p>}
          {activity.slice(0, 50).map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 border-b border-slate-50 px-4 py-2.5 text-sm last:border-0">
              <span className="text-xs text-slate-400">{fmtDateTime(a.created_at)}</span>
              <span className="font-semibold text-slate-700">{a.user_name}</span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{a.action}</span>
              <span className="text-slate-500">{a.entity}</span>
              <span className="text-xs text-slate-400 truncate">{a.details}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
